import React, { useEffect } from 'react';
import { AlertCircle, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSocialFeed } from '../hooks/useSocialFeedRest';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import POSTComponent from '../components/POSTComponent';
import { usePageRefresh } from '../../contexts/RefreshContext';

const SocialFeedPage = () => {
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();

  const POST_MAX_LENGTH = parseInt(import.meta.env.REACT_APP_POST_MAX_LENGTH) || 4000;

  const { isAuthenticated, authChecked, user } = useAuth();
  const shouldLoadSocial = authChecked && isAuthenticated;
  const { posts, loading, error, createPost, reactToPost, deletePost, updatePost, refreshFeed } = useSocialFeed(10, shouldLoadSocial);

  const currentUserId = user?.id ? String(user.id) : null;

  usePageRefresh(refreshFeed);

  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Home', path: '/' },
      { label: 'Feeds' }
    ]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  const handleCreatePost = async (text, imageUrls, privacy) => {
    return createPost({ text, imageUrls, privacy });
  };

  const handleReact = async (postId, type) => {
    const result = await reactToPost(postId, type);
    if (!result.success) {
      console.error('Failed to react to post:', result.error);
    }
  };

  if (!authChecked) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (authChecked && !isAuthenticated) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-8 text-center max-w-md mx-4`}>
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text} mb-4`}>Authentication Required</h2>
          <p className={`${theme.textMuted} mb-6`}>
            You need to be logged in to view the social feed. Redirecting to sign-in page...
          </p>
          <Loader className="w-6 h-6 animate-spin text-blue-500 mx-auto" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${theme.background}`}>
        <main className="max-w-2xl mx-auto px-4 py-6">
          <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 text-center`}>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className={`text-xl font-semibold ${theme.text} mb-2`}>Connection Failed</h2>
            <p className={`${theme.textMuted} mb-2`}>Could not load your feed. Please check your connection.</p>
            <p className={`${theme.textMuted} mb-4 text-sm`}>Error: {error.message}</p>
            <button
              onClick={refreshFeed}
              className={`${theme.gradient} text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-shadow`}
            >
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <main className="max-w-7xl mx-auto px-4 lg:px-0 py-6">
        <POSTComponent
          posts={posts}
          loading={loading}
          canPost={true}
          onCreatePost={handleCreatePost}
          onReactToPost={handleReact}
          onDeletePost={deletePost}
          onPostUpdated={updatePost}
          currentUserId={currentUserId}
          placeholder="What's on your mind?"
          maxLength={POST_MAX_LENGTH}
          defaultPrivacy="public"
          emptyMessage="Share your first sports moment to get started!"
        />
      </main>
    </div>
  );
};

export default SocialFeedPage;
